import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { Checkbox, TableHead, TableRow } from "@mui/material";
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import TableCell from "../components/TableCell";
import Context from "../context-index";
import { Column, GridProps, RowActions, RowActionsExtra, SortColumn, SortType } from "../export";
import { getBrandCustom } from "../utils/getCustomColor";
import ColumnChooser from "./ColumnChooser";
import ColumnHeader from "./ColumnHeader";
import useStyles from "./styles";
import { colors } from "@zapalacorp/tokens";

export interface GridHeaderHandles {
  verifyIfHasSelected: (dataSize: number, selectedList: number[], searching: boolean) => void;
}

type Props = {
  loadingData: boolean;
  columns: Column[];
  actions?: RowActions;
  extraActions?: RowActionsExtra[];
  positionActions: "left" | "right";
  searching: boolean;
  showSelection: boolean;
  handleSelectAllRows: Function;
  showSort: boolean;
  handleSortingData: Function;
  showActionLabel?: GridProps["showActionLabel"];
  enableGetHeaderHeight?: boolean;
  pinnedTable?: boolean;
  showColumnChooser: boolean;
  endScrolled?: boolean;
  stickyActions: boolean;
  gridCellPadding?: string;
};

const GridHeader: ForwardRefRenderFunction<GridHeaderHandles, Props> = (
  {
    loadingData,
    columns,
    actions,
    extraActions,
    positionActions,
    searching,
    showSelection,
    handleSelectAllRows,
    showSort,
    handleSortingData,
    showActionLabel,
    enableGetHeaderHeight,
    pinnedTable,
    showColumnChooser,
    endScrolled,
    stickyActions,
    gridCellPadding,
  }: Props,
  ref
) => {
  useImperativeHandle(ref, () => {
    return {
      verifyIfHasSelected,
    };
  });

  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [checked, setChecked] = useState(false);
  const [columnClicked, setColumnClicked] = useState<SortColumn>({
    columnName: "none",
    direction: SortType.ASC,
    columnToSorting: undefined,
  });

  const [hasSelected, setHasSelected] = useState(false);

  const { selectedColumns, headerHeight, setHeaderHeight } = useContext(Context);

  function verifyIfHasSelected(dataSize: number, selectedList: number[]) {
    selectedList.length > 0 ? setHasSelected(true) : setHasSelected(false);
    if (selectedList.length < dataSize && !searching) {
      setChecked(false);
    }

    if (selectedList.length === dataSize && dataSize !== 0) {
      setChecked(true);
    }
  }

  const headerRef = useRef<HTMLTableRowElement>(null);

  const getRowHeight = () => {
    const element = headerRef?.current;

    if (element && (headerHeight !== element?.offsetHeight || !headerHeight))
      setHeaderHeight(element?.offsetHeight);
  };

  useEffect(() => {
    if (enableGetHeaderHeight) {
      getRowHeight();
      window.addEventListener("resize", getRowHeight);
    }
  }, [enableGetHeaderHeight, headerRef?.current?.offsetHeight]);

  useEffect(() => {
    if (searching == false) {
      setChecked(false);
    }
  }, [searching]);

  useEffect(() => {
    if (columnClicked.columnName !== "none") {
      const sort: SortColumn[] = [
        {
          columnName: columnClicked.columnName,
          direction: columnClicked.direction,
          columnToSorting: columnClicked.columnToSorting,
        },
      ];

      handleSortingData(sort);
    }
  }, [columnClicked]);

  const renderCheckbox = () => {
    if (showSelection && selectedColumns.length >= 1) {
      return (
        <TableCell data-cy="checkbox" width="50px" padding="none" sx={{ padding: gridCellPadding }}>
          <div className={classes.checkboxCell}>
            <Checkbox
              key="checkbox"
              disabled={loadingData}
              checked={checked}
              color="default"
              indeterminate={!checked && hasSelected}
              icon={<CheckBoxOutlineBlankIcon htmlColor="#D6DAE1" />}
              checkedIcon={
                <CheckBoxIcon htmlColor={getBrandCustom("primaryColor", colors.brandBlue500)} />
              }
              indeterminateIcon={
                <IndeterminateCheckBoxIcon
                  htmlColor={getBrandCustom("primaryColor", colors.brandBlue500)}
                />
              }
              onChange={(event) => handleSelectAllRows(event.target.checked)}
            />
          </div>
        </TableCell>
      );
    }

    return null;
  };

  const renderActions = (position: "right" | "left") => {
    if (
      positionActions === position &&
      (actions || extraActions) &&
      selectedColumns.length >= 1 &&
      showActionLabel
    ) {
      return (
        <TableCell
          id="actions-right"
          endScrolled={endScrolled}
          sticky={stickyActions}
          data-cy={`actions-${position}`}
          width="150px"
          key="actions"
          sx={{ padding: gridCellPadding }}
          isAction
        >
          <div
            data-cy="container-actions"
            style={{ paddingRight: showColumnChooser ? "20px" : "" }}
            className={classes.containerActions}
          >
            <span data-cy="text-actions" className={classes.actions}>
              {translate("actions")}
            </span>
          </div>
          {stickyActions && renderColumnChooser("div")}
        </TableCell>
      );
    }

    return null;
  };

  const renderColumnChooser = (tag: "div" | "th") => {
    if (!showColumnChooser || !columns || !columns?.length) return;

    const CustomTag = tag as keyof JSX.IntrinsicElements;

    return (
      <CustomTag className={classes.columnChooser}>
        <ColumnChooser columns={columns} />
      </CustomTag>
    );
  };

  return (
    <TableHead data-cy="table-head">
      <TableRow
        data-cy="table-row"
        className="tr-header"
        ref={headerRef}
        style={{
          ...(pinnedTable && headerHeight ? { height: headerHeight } : {}),
          position: "relative",
        }}
      >
        {renderCheckbox()}
        {renderActions("left")}
        {columns.map((column, index) => {
          const theLastColumn = index === columns.length - 1;
          const wasSelected = selectedColumns.some((column) => column === index) ? "" : "none";
          const applyPaddingRightToLastElement =
            showColumnChooser && theLastColumn && !(actions || extraActions) ? "20px" : "";
          return (
            <TableCell
              padding="none"
              data-cy={column?.name && column?.name?.toLowerCase()}
              key={index}
              style={{
                display: wasSelected,
                padding: gridCellPadding ?? "0 8px",
                ...(applyPaddingRightToLastElement
                  ? { paddingRight: applyPaddingRightToLastElement }
                  : {}),
                ...column?.style,
              }}
              className={column.className}
              sticky={!loadingData && !!column.stickyRight}
              right={column.stickyRight}
              endScrolled={endScrolled}
            >
              <ColumnHeader
                column={column}
                key={`${column.name}-${column.title}`}
                showSort={showSort}
                columnClicked={columnClicked}
                setColumnClicked={setColumnClicked}
              />
            </TableCell>
          );
        })}
        {renderActions("right")}
        {!stickyActions && renderColumnChooser("th")}
      </TableRow>
    </TableHead>
  );
};

export default forwardRef(GridHeader);
